import { useMemo } from "react";
import {
  evidenceTableClasses,
  markdownTableCellClasses,
  markdownTableHeadClasses,
  markdownTableHeaderCellClasses,
} from "@/components/ui/markdown-table-classes";
import { cn } from "@/lib/utils";

/**
 * A quoted table keeps its own shape, so the box around it is what gives way: it scrolls in
 * both directions inside the popover rather than letting a wide table push the popover wider
 * or a long one push the rest of the citation off screen.
 */
const evidenceTableBoxClasses = "max-h-[10.125rem] max-w-full overflow-auto border border-border";

/**
 * Verbatim rendering for evidence excerpts (LAT-951). An excerpt is a quote, so it is
 * never run through a markdown renderer — that would re-interpret emphasis or headings
 * inside the quoted text and falsify it. Only GFM *table structure* is recognized, with
 * a line-based parser tolerant of the fragments evidence actually produces: mid-table
 * claim spans with no header row, rows clipped with a trailing ellipsis, and prose or
 * truncation markers around the table.
 */

export type EvidenceExcerptSegment =
  | { kind: "text"; text: string }
  | { kind: "table"; header: string[] | null; rows: string[][] };

const TABLE_ROW_LINE = /^\s*\|/;
const TABLE_SEPARATOR_LINE = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;

/**
 * Ingestion noise, not document content: a stored revision can hold docling's
 * `<!-- rich cell -->` marker where a styled table cell's text should be, and stays
 * readable while anything cites it. The chat renderer already drops HTML comments via
 * markdown; the verbatim table renderer must not show them as source text. A clipped
 * cell can hold an unterminated comment, so the close is optional. Remove once every
 * environment's `reprocessCapabilityStaleRevisions` sweep reports no candidates.
 */
const HTML_COMMENT = /<!--[\s\S]*?(?:-->|$)/g;

function cleanCellText(cell: string): string {
  return cell.replace(HTML_COMMENT, "").trim();
}

// Inverse of the backend's renderTableRowLine: outer pipes are delimiters and `\|`
// escapes a literal pipe inside a cell. A clipped row may have lost its trailing pipe.
function splitRowCells(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith("|")) trimmed = trimmed.slice(1);
  if (trimmed.endsWith("|")) trimmed = trimmed.slice(0, -1);
  const cells: string[] = [];
  let current = "";
  for (let index = 0; index < trimmed.length; index += 1) {
    const char = trimmed[index];
    if (char === "\\" && trimmed[index + 1] === "|") {
      current += "|";
      index += 1;
    } else if (char === "|") {
      cells.push(cleanCellText(current));
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(cleanCellText(current));
  return cells;
}

/**
 * GFM forces every table to carry a header row, so ingestion promotes the first DATA
 * row of a headerless source table into the header position. A real header names its
 * columns in short labels; a promoted data row reads like data. Emphasized styling for
 * the latter presents data as structure, so it is demoted back into the body.
 */
function isPlausibleHeader(cells: string[]): boolean {
  const nonEmpty = cells.filter((cell) => cell.length > 0);
  if (nonEmpty.length === 0) return false;
  return nonEmpty.every((cell) => cell.length <= 60 && !/[.!?]$/.test(cell));
}

function buildTableSegment(run: string[]): EvidenceExcerptSegment | null {
  const hasHeaderRow = run.length > 1 && TABLE_SEPARATOR_LINE.test(run[1]!);
  let header = hasHeaderRow ? splitRowCells(run[0]!) : null;
  let rows = (hasHeaderRow ? run.slice(2) : run)
    .filter((line) => !TABLE_SEPARATOR_LINE.test(line))
    .map(splitRowCells);
  if (header && !isPlausibleHeader(header)) {
    rows = [header, ...rows];
    header = null;
  }
  // An all-empty row carries no information — most often a real header whose styled
  // cells ingestion replaced with rich-cell markers, demoted above and now blank. The
  // table keeps its data rows and honestly shows no header at all.
  rows = rows.filter((row) => row.some((cell) => cell.length > 0));

  // Same rule per column (typically an all-marker label or numbering column): pruning it
  // is what turns a half-lost table into a clean grid instead of one with a blank flank.
  const columnCount = Math.max(header?.length ?? 0, ...rows.map((row) => row.length), 1);
  const keptColumns = Array.from({ length: columnCount }, (_, column) => column).filter((column) =>
    [...(header ? [header] : []), ...rows].some((cells) => (cells[column] ?? "").length > 0)
  );
  // Nothing survivable: no columns at all, or no rows AND no header (a header-only
  // fragment is still a legitimate quote of a header-only span).
  if (keptColumns.length === 0 || (rows.length === 0 && !header)) return null;
  const project = (cells: string[]) => keptColumns.map((column) => cells[column] ?? "");

  return {
    kind: "table",
    header: header ? project(header) : null,
    rows: rows.map(project),
  };
}

export function parseEvidenceExcerptSegments(excerpt: string): EvidenceExcerptSegment[] {
  const segments: EvidenceExcerptSegment[] = [];
  let textLines: string[] = [];
  let rowLines: string[] = [];

  const flushText = () => {
    if (textLines.length === 0) return;
    segments.push({ kind: "text", text: textLines.join("\n") });
    textLines = [];
  };
  const flushRows = () => {
    if (rowLines.length === 0) return;
    // A lone pipe-prefixed line with no delimiter row is prose that happens to start
    // with a pipe, not a one-row table.
    if (rowLines.length === 1 && !TABLE_SEPARATOR_LINE.test(rowLines[0]!)) {
      textLines.push(rowLines[0]!);
    } else {
      const table = buildTableSegment(rowLines);
      if (table) {
        flushText();
        segments.push(table);
      }
    }
    rowLines = [];
  };

  const lines = excerpt.split("\n");
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!;
    // A claims span is a byte-exact slice, so it can start mid-cell: the first row of
    // the fragment then has no leading pipe. A line that ends in a pipe and leads
    // straight into further table rows is that partial edge row, not prose.
    const isLeadingPartialRow =
      rowLines.length === 0 &&
      /\|\s*$/.test(line) &&
      index + 1 < lines.length &&
      TABLE_ROW_LINE.test(lines[index + 1]!);
    if (TABLE_ROW_LINE.test(line) || isLeadingPartialRow) {
      rowLines.push(line);
    } else {
      flushRows();
      textLines.push(line);
    }
  }
  flushRows();
  flushText();
  return segments;
}

/** Whether an excerpt renders any table — a shape of its own rather than quoted prose. */
export function excerptContainsTable(excerpt: string): boolean {
  return parseEvidenceExcerptSegments(excerpt).some((segment) => segment.kind === "table");
}

/** Which slice of a bigger table the quote holds, when it holds only a slice. */
export interface EvidenceTableRowRange {
  start: number;
  end: number;
  total: number;
}

/**
 * A table too large to ingest as one block is split at ingestion, and each part carries the
 * range it covers as a line of italic prose (`transforms.ts`, `splitTableBlockForRender`).
 * That line is the only record of the crop, so the popover reads the range out of it and
 * restates it as the table's caption rather than leaving it to be read as table content.
 *
 * A table that was never split says nothing, and then neither does the popover.
 */
const TABLE_ROW_ANNOTATION = /^[ \t]*_?Table rows (\d+)[\u2013\u2014-](\d+) of (\d+)\.?_?[ \t]*$/mu;

export function parseEvidenceTableRowRange(excerpt: string): EvidenceTableRowRange | null {
  const match = TABLE_ROW_ANNOTATION.exec(excerpt);
  if (!match) return null;
  const [start, end, total] = [Number(match[1]), Number(match[2]), Number(match[3])];
  // A range that does not describe a crop is not worth a caption.
  if (!(start >= 1 && end >= start && total >= end)) return null;
  return { start, end, total };
}

/**
 * The excerpt without the row-range line, for when the popover has promoted that line to a
 * caption. Only the annotation goes; every other line of the quote is left exactly as it is.
 */
export function stripEvidenceTableRowAnnotation(excerpt: string): string {
  if (!TABLE_ROW_ANNOTATION.test(excerpt)) return excerpt;
  return excerpt
    .split("\n")
    .filter((line) => !TABLE_ROW_ANNOTATION.test(line))
    .join("\n")
    .replace(/\n{3,}/gu, "\n\n")
    .trim();
}

/**
 * Renders an evidence excerpt: table fragments as real compact tables whose cells wrap
 * (reading down beats scrolling sideways in a popover; the scroll wrapper remains only
 * as the safety valve for genuinely many-column tables), everything else as the
 * verbatim text it was. Prose-only excerpts return the bare string so existing
 * containers render exactly as they did before tables were recognized.
 */
export function EvidenceExcerpt({ excerpt }: { excerpt: string }) {
  const segments = useMemo(() => parseEvidenceExcerptSegments(excerpt), [excerpt]);
  if (!segments.some((segment) => segment.kind === "table")) {
    return excerpt;
  }

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {segments.map((segment, index) =>
        segment.kind === "text" ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are derived from a stable string and never reorder.
          <p key={index} className="whitespace-pre-wrap break-words">
            {segment.text}
          </p>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are derived from a stable string and never reorder.
          <div key={index} className={evidenceTableBoxClasses}>
            <table className={evidenceTableClasses}>
              {segment.header ? (
                <thead className={markdownTableHeadClasses}>
                  <tr>
                    {segment.header.map((cell, cellIndex) => (
                      <th
                        // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional by construction.
                        key={cellIndex}
                        className={cn(markdownTableHeaderCellClasses, "break-words")}
                      >
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {segment.rows.map((row, rowIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional by construction.
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional by construction.
                      <td key={cellIndex} className={cn(markdownTableCellClasses, "break-words")}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
